// services/credit.service.js

import { 
    collection, 
    addDoc, 
    query, 
    where, 
    getDocs,
    updateDoc,
    doc,
    getDoc,
    Timestamp,
    increment 
} from 'firebase/firestore';
import { db } from '../config/firebase';

export const creditService = {
    // Get user's current credit balance
    getUserCredits: async (userId) => {
        try {
            const subscriptionQuery = query(
                collection(db, "subscriptions"),
                where("userId", "==", userId),
                where("status", "==", "active")
            );
            const subscriptionSnapshot = await getDocs(subscriptionQuery);
            
            if (subscriptionSnapshot.empty) {
                return null;
            }

            const subscription = subscriptionSnapshot.docs[0].data();
            
            return {
                availableCredits: subscription.availableCredits || 0,
                monthlyCredits: subscription.monthlyCredits,
                rolloverCredits: subscription.rolloverCredits || 0,
                purchasedCredits: subscription.purchasedCredits || 0,
                nextRenewal: subscription.nextRenewal,
            };
        } catch (error) {
            throw error;
        }
    },

    // Purchase additional credits
    purchaseCredits: async (userId, hours) => {
        try {
            const subscriptionQuery = query(
                collection(db, "subscriptions"),
                where("userId", "==", userId),
                where("status", "==", "active")
            );
            const subscriptionSnapshot = await getDocs(subscriptionQuery);
            
            if (subscriptionSnapshot.empty) {
                throw new Error("No active subscription found");
            }

            const subscriptionDoc = subscriptionSnapshot.docs[0];
            const subscription = subscriptionDoc.data();

            // Validate against monthly limit
            const currentMonth = new Date().getMonth();
            const purchaseHistory = await getDocs(query(
                collection(db, "creditPurchases"),
                where("userId", "==", userId),
                where("purchaseMonth", "==", currentMonth)
            ));

            const purchasedThisMonth = purchaseHistory.docs.reduce(
                (total, doc) => total + doc.data().hours, 0
            );

            if (purchasedThisMonth + hours > subscription.features.addOnCredits.limit) {
                throw new Error(`Monthly purchase limit reached. Maximum ${subscription.features.addOnCredits.limit} hours per month.`);
            }

            // Record the purchase
            await addDoc(collection(db, "creditPurchases"), {
                userId,
                hours,
                purchaseMonth: currentMonth,
                purchaseDate: Timestamp.now(),
                subscriptionId: subscriptionDoc.id,
                pricePerHour: subscription.features.addOnCredits.price,
                status: "pending" // Will be updated to "completed" after payment
            });

            return true;
        } catch (error) {
            throw error;
        }
    },

    // Validate credits for booking
    validateBooking: async (userId, requestedHours) => {
        try {
            const credits = await creditService.getUserCredits(userId);
            
            if (!credits) {
                return {
                    valid: false,
                    message: "No active subscription found"
                };
            }

            if (credits.availableCredits < requestedHours) {
                return {
                    valid: false,
                    message: `Insufficient credits. Available: ${credits.availableCredits} hours`,
                    suggestPurchase: true,
                    shortfall: requestedHours - credits.availableCredits
                };
            }

            return {
                valid: true,
                availableCredits: credits.availableCredits
            };
        } catch (error) {
            throw error;
        }
    },

    // Process monthly credit renewal
    processMonthlyRenewal: async (subscriptionId) => {
        try {
            const subscriptionDoc = await getDoc(doc(db, "subscriptions", subscriptionId));
            const subscription = subscriptionDoc.data();

            const unusedCredits = subscription.availableCredits || 0;
            const monthlyCredits = subscription.monthlyCredits;

            await updateDoc(doc(db, "subscriptions", subscriptionId), {
                availableCredits: monthlyCredits + unusedCredits,
                rolloverCredits: unusedCredits,
                nextRenewal: Timestamp.fromDate(
                    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
                ),
                lastRenewal: Timestamp.now()
            });

            return true;
        } catch (error) {
            throw error;
        }
    },

    // Deduct credits after session
    deductCredits: async (userId, sessionHours) => {
        try {
            const subscriptionQuery = query(
                collection(db, "subscriptions"),
                where("userId", "==", userId),
                where("status", "==", "active")
            );
            const subscriptionSnapshot = await getDocs(subscriptionQuery);
            
            if (subscriptionSnapshot.empty) {
                throw new Error("No active subscription found");
            }

            const subscriptionDoc = subscriptionSnapshot.docs[0];

            await updateDoc(doc(db, "subscriptions", subscriptionDoc.id), {
                availableCredits: increment(-sessionHours)
            });

            return true;
        } catch (error) {
            throw error;
        }
    },

    approveCreditRequest: async (requestId) => {
        try {
            const requestDoc = await getDoc(doc(db, "creditRequests", requestId));
            const request = requestDoc.data();
    
            // Only admins can write to subscriptions
            await updateDoc(doc(db, "subscriptions", request.subscriptionId), {
                purchasedCredits: increment(request.hours),
                availableCredits: increment(request.hours)
            });
    
            // Update request status
            await updateDoc(doc(db, "creditRequests", requestId), {
                status: "approved",
                approvedAt: Timestamp.now()
            });
    
            return true;
        } catch (error) {
            throw error;
        }
    }
};